<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { debounce, formatKey } from '@shared/utils'
import { toErrorMessage } from '@core/api/normalizeError'
import { useAppToast } from '@shared/composables/useAppToast'
import { useVaspWalletListQuery } from '../api/queries/useVaspWalletListQuery'
import { useVaspWalletStatusCountsQuery } from '../api/queries/useVaspWalletStatusCountsQuery'
import { useSyncVaspWalletMutation } from '../api/mutations/useSyncVaspWalletMutation'
import { useVaspWalletDrawer } from '../composables/useVaspWalletDrawer'
import {
  CRYPTO_WALLET_STATUS,
  VASP_WALLET_CARD_STATUS_FILTERS,
  type VaspWalletCardKey,
} from '../constants/cryptoWalletStatus'
import type { VaspWalletListItem } from '../types/cryptoWalletTypes'

import VaspWalletQueueList from '../components/VaspWalletQueueList.vue'
import VaspWalletDrawerDetails from '../components/VaspWalletDrawerDetails.vue'

import Box from '@uiKit/elements/Box.vue'
import Button from '@uiKit/elements/Button.vue'
import InfoBanner from '@uiKit/components/InfoBanner.vue'
import KpiCard from '@uiKit/components/KpiCard.vue'
import AppDrawer from '@uiKit/elements/Drawer.vue'
import DrawerHeader from '@uiKit/components/DrawerHeader.vue'
import Avatar from '@uiKit/components/Avatar.vue'

const SEARCH_DEBOUNCE_MS = 400
const ID_SUFFIX_LENGTH = 6

// ─── Drawer state ─────────────────────────────────────────────────────────────
const {
  drawerVisible,
  selectedWallet,
  details,
  detailsLoading,
  refetchDetails,
  openDrawer,
  closeDrawer,
} = useVaspWalletDrawer()

const { showSuccess, showError, showInfo } = useAppToast()

function onRowClick(wallet: VaspWalletListItem) {
  clearAttemptError()
  openDrawer(wallet)
}

function handleCloseDrawer() {
  clearAttemptError()
  closeDrawer()
}

// ─── Last failed attempt (transient) ─────────────────────────────────────────
// The persisted `lastError` only reaches the drawer after the refetch, so show
// the message from the attempt that just failed straight away. Keyed to the row
// it belongs to: even if a reset is missed, it can never surface on another
// wallet. Cleared on drawer open, drawer close, and any successful sync.
const attemptError = ref<string | null>(null)
const attemptErrorForId = ref('')

const visibleAttemptError = computed(() =>
  details.value && attemptErrorForId.value === details.value.id ? attemptError.value : null,
)

function setAttemptError(rowId: string, message: string) {
  attemptError.value = message
  attemptErrorForId.value = rowId
}

function clearAttemptError() {
  attemptError.value = null
  attemptErrorForId.value = ''
  statusAtSync.value = ''
}

// The wallet's status as the operator saw it when they pressed Sync, compared
// against the refetched row. When it moved underneath them, the refusal is about
// a state they could not see — retrying the same action fails identically, and
// refreshing is the affordance that helps.
const statusAtSync = ref('')

const isStaleView = computed(() => {
  const row = details.value
  if (!row || !visibleAttemptError.value || !statusAtSync.value) return false
  return row.walletStatus !== statusAtSync.value
})

// ─── Sync ─────────────────────────────────────────────────────────────────────
const { mutate: syncWallet } = useSyncVaspWalletMutation()

function handleSyncRow(wallet: VaspWalletListItem) {
  statusAtSync.value = wallet.walletStatus
  syncWallet(wallet.id, {
    onSuccess: (result) => {
      clearAttemptError()
      // A sync is a read-and-reconcile, so "nothing changed" is a successful and
      // common outcome. Saying "synced" alone would leave the operator wondering
      // whether the click registered.
      if (result.walletStatus === wallet.walletStatus) {
        showInfo(
          'Already up to date',
          `Ctrl-Alt still reports this wallet as ${formatKey(result.walletStatus)}. Nothing changed.`,
        )
        return
      }
      showSuccess(
        'Wallet synced',
        result.walletStatus === CRYPTO_WALLET_STATUS.ACTIVE
          ? 'Ctrl-Alt has validated this wallet — it is now active.'
          : `Status updated from Ctrl-Alt to ${formatKey(result.walletStatus)}.`,
      )
    },
    onError: (err: unknown) => {
      const message = toErrorMessage(err, 'An error occurred while syncing the wallet.')
      setAttemptError(wallet.id, message)
      // Surface the reason where the operator can read it in full and act on it,
      // not just in a toast that disappears.
      if (!drawerVisible.value || details.value?.id !== wallet.id) openDrawer(wallet)
      showError('Sync failed', message)
    },
  })
}

// ─── Drawer chrome ────────────────────────────────────────────────────────────
const drawerTitle = computed(() =>
  selectedWallet.value
    ? `${selectedWallet.value.firstName} ${selectedWallet.value.lastName}`.trim() || '—'
    : '—',
)

const drawerSubtitle = computed(() => {
  const wallet = selectedWallet.value
  if (!wallet) return ''
  return `WLT-${wallet.id.slice(-ID_SUFFIX_LENGTH).toUpperCase()} · ${wallet.blockchainNetwork || 'Wallet'}`
})

// ─── Search & pagination (server-side) ───────────────────────────────────────
const searchQuery = ref('')
const debouncedSearch = ref('')
const currentPage = ref(1)
const rowsPerPage = ref(10)

const updateDebouncedSearch = debounce((value: string) => {
  debouncedSearch.value = value
  // A narrower result set can be shorter than the page the operator is on, which
  // would otherwise render an empty table on page 3 of a 1-page result.
  currentPage.value = 1
}, SEARCH_DEBOUNCE_MS)

watch(searchQuery, (value) => updateDebouncedSearch(value))

// ─── Status filter cards ──────────────────────────────────────────────────────
// All entries · Pending validation · Active · Inactive. Clicking a card narrows
// the queue to that bucket (VASP_WALLET_CARD_STATUS_FILTERS). Card counts come
// from a dedicated query and stay constant regardless of the active card or
// search — they are the queue's shape, not the current view's.
const activeCard = ref<VaspWalletCardKey>('all')

function selectCard(card: VaspWalletCardKey) {
  activeCard.value = card
  currentPage.value = 1
}

const { data: counts, isLoading: countsLoading } = useVaspWalletStatusCountsQuery()

// ─── Query — wallets minted through VAISP onboarding ─────────────────────────
const filters = computed(() => ({
  page: currentPage.value,
  limit: rowsPerPage.value,
  search: debouncedSearch.value || undefined,
  walletStatus: VASP_WALLET_CARD_STATUS_FILTERS[activeCard.value],
}))

const {
  data,
  isLoading,
  isFetching,
  isError,
  error,
  refetch: refetchList,
} = useVaspWalletListQuery(filters)

// The empty state and a failed fetch are different answers, and the table can
// only tell the operator one of them. Without this branch a 500 renders as "No
// wallets in this queue" — a queue reported as clear when it is actually
// unknown, which is the worst possible lie to tell someone clearing a backlog.
const listErrorMessage = computed(() =>
  isError.value ? toErrorMessage(error.value, 'Could not load the VAISP wallet queue.') : null,
)
</script>

<template>
  <Box flex direction="col" gap="4">
    <Box flex direction="row" gap="3">
      <KpiCard
        label="ALL ENTRIES"
        :value="counts?.all ?? 0"
        :loading="countsLoading"
        clickable
        :selected="activeCard === 'all'"
        @click="selectCard('all')"
      />
      <KpiCard
        label="PENDING VALIDATION"
        :value="counts?.pendingValidation ?? 0"
        :loading="countsLoading"
        clickable
        :selected="activeCard === 'pending'"
        @click="selectCard('pending')"
      />
      <KpiCard
        label="ACTIVE"
        :value="counts?.active ?? 0"
        :loading="countsLoading"
        clickable
        :selected="activeCard === 'active'"
        @click="selectCard('active')"
      />
      <KpiCard
        label="INACTIVE"
        :value="counts?.inactive ?? 0"
        :loading="countsLoading"
        clickable
        :selected="activeCard === 'inactive'"
        @click="selectCard('inactive')"
      />
    </Box>

    <InfoBanner
      v-if="listErrorMessage"
      variant="danger"
      icon="exclamation-triangle"
      title="Couldn't load the queue"
    >
      <template #default>{{ listErrorMessage }}</template>
      <template #actions>
        <Button
          label="Try again"
          icon="refresh"
          color="secondary"
          variant="outline"
          size="small"
          :loading="isFetching"
          @click="refetchList()"
        />
      </template>
    </InfoBanner>

    <VaspWalletQueueList
      :rows="data?.rows ?? []"
      :loading="isLoading || isFetching"
      :total="data?.rowsCount ?? 0"
      :page="currentPage"
      :page-size="rowsPerPage"
      :search="searchQuery"
      @update:search="searchQuery = $event"
      @update:page="currentPage = $event"
      @row-click="onRowClick"
      @view-details="onRowClick"
      @sync="handleSyncRow"
    />
  </Box>

  <AppDrawer
    :model-value="drawerVisible"
    position="right"
    width="480px"
    :show-close-icon="false"
    :loading="detailsLoading"
    @update:model-value="(v: boolean) => !v && handleCloseDrawer()"
  >
    <template #header>
      <DrawerHeader :title="drawerTitle" :subtitle="drawerSubtitle" @close="handleCloseDrawer">
        <template #avatar>
          <Avatar
            :id="selectedWallet?.investorId ?? ''"
            :name="`${selectedWallet?.firstName ?? ''} ${selectedWallet?.lastName ?? ''}`"
          />
        </template>
      </DrawerHeader>
    </template>

    <VaspWalletDrawerDetails
      v-if="details"
      :details="details"
      :attempt-error="visibleAttemptError"
      :stale="isStaleView"
      @refresh="refetchDetails"
    />
  </AppDrawer>
</template>
